import { SxProps } from "@mui/material";
import {
  chartsAxisHighlightClasses,
  CurveType,
  LineChart,
  LineChartProps,
  lineClasses,
  XAxis,
  YAxis,
} from "@mui/x-charts";
import {
  cardContentHeight,
  DashboardCard,
} from "@pages:app/analytics/components/cards/DashboardCard";
import { symlog, symlogInvert } from "@pages:app/analytics/utils";

const TITLE_BLOCK = 40;
const PADDING_Y = 36;

const defaultChartSx: SxProps = {
  [`.${lineClasses.line}`]: { strokeWidth: 3 },
  [`.${chartsAxisHighlightClasses.root}`]: { strokeWidth: 2 },
};

type YScale = "linear" | "log" | "symlog";

type ChartCardProps = {
  title: string;
  xAxis: ReadonlyArray<XAxis> | undefined;
  yAxis: ReadonlyArray<YAxis> | undefined;
  series: LineChartProps["series"];
  colSpan?: number;
  rowSpan?: number;
  hideLegend?: boolean;
  chartSx?: SxProps;
  smooth?: boolean;
  integerYTicks?: boolean;
  yScale?: YScale;
  yValueFormatter?: (value: number) => string;
};

export const ChartCard = ({
  title,
  xAxis,
  yAxis,
  series,
  colSpan = 12,
  rowSpan = 2,
  hideLegend = true,
  chartSx = defaultChartSx,
  smooth = true,
  integerYTicks,
  yScale = "linear",
  yValueFormatter,
}: ChartCardProps) => {
  const curve: CurveType = smooth ? "monotoneX" : "linear";
  const formatReal = (value: number) => {
    const rounded = Math.round(value);
    return yValueFormatter
      ? yValueFormatter(rounded)
      : rounded.toLocaleString();
  };

  // X Charts v9 hides marks by default and varies their shape per series
  const styledSeries = series?.map((seriesEntry) => {
    const entry = {
      showMark: true,
      shape: "circle" as const,
      ...seriesEntry,
    };

    if (!Array.isArray(entry.data)) {
      return { ...entry, curve };
    }

    if (yScale === "symlog") {
      return {
        ...entry,
        curve,
        data: entry.data.map((value) => (value == null ? null : symlog(value))),
        valueFormatter: (value: number | null) =>
          value == null ? "" : formatReal(symlogInvert(value)),
      };
    }

    if (yScale === "log") {
      return {
        ...entry,
        curve,
        data: entry.data.map((value) => (value === 0 ? null : value)),
      };
    }

    return {
      ...entry,
      curve,
      ...(yValueFormatter && {
        valueFormatter: (value: number | null) =>
          value == null ? "" : yValueFormatter(Math.round(value)),
      }),
    };
  });

  // Pin symlog ticks to clean powers of ten
  const symlogTicks =
    yScale === "symlog"
      ? (() => {
          const max = Math.max(
            10,
            ...(series ?? []).flatMap((entry) =>
              Array.isArray(entry.data)
                ? entry.data.map((value) =>
                    typeof value === "number" ? value : 0,
                  )
                : [],
            ),
          );

          const top = Math.ceil(Math.log10(max));
          const exponents = new Set<number>([0, top]);
          for (let k = 2; k <= top; k += 2) {
            exponents.add(k);
          }

          return [...exponents]
            .sort((a, b) => a - b)
            .map((k) => symlog(k === 0 ? 0 : 10 ** k));
        })()
      : undefined;

  const styledYAxis = yAxis?.map((axis) => {
    if (yScale === "symlog") {
      return {
        ...axis,
        scaleType: "linear" as const,
        min: 0,
        tickInterval: symlogTicks,
        valueFormatter: (value: number | null) =>
          value == null ? "" : formatReal(symlogInvert(value)),
      };
    }

    return {
      ...axis,
      ...(integerYTicks && { tickMinStep: 1 }),
      ...(yScale === "log" && { scaleType: "log" as const, min: undefined }),
      ...(yValueFormatter && {
        valueFormatter: (value: number | null) =>
          value == null ? "" : yValueFormatter(value),
      }),
    };
  }) as ReadonlyArray<YAxis> | undefined;

  return (
    <DashboardCard
      title={title}
      colSpan={colSpan}
      rowSpan={rowSpan}
      sx={{ gap: "8px" }}
    >
      <LineChart
        sx={chartSx}
        skipAnimation
        xAxis={xAxis}
        yAxis={styledYAxis}
        series={styledSeries}
        height={cardContentHeight(rowSpan) - TITLE_BLOCK - PADDING_Y}
        hideLegend={hideLegend}
        margin={{ right: 32 }}
      />
    </DashboardCard>
  );
};
